import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  MatLegacySnackBar as MatSnackBar,
  MatLegacySnackBarConfig as MatSnackBarConfig,
} from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { UserActivationPayload } from '@shared/models/activation.models';
import { ActivationService } from '@shared/services/activation.service';
import { DataLayerService } from '@shared/services/data-layer.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ActivateValidationErrorComponent } from '@shared/shared/components/activate-validation-error/activate-validation-error.component';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { IUser } from '@shared/shared/interfaces/user.interfaces';
import { replaceTemplateKeys } from '@shared/utils/url.lib';
import { Angulartics2Segment } from 'angulartics2';
import { USER_LOCAL_STORAGE_KEY } from 'app.config';
import { CookieService } from 'ngx-cookie-service';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as MemberProfileStore from '../../member-profile';
import * as UserPasswordActions from '../actions/user-password.actions';
import { UserState } from '../reducers';

@Injectable()
export class UserPasswordEffects {
  constructor(
    private actions$: Actions,
    private dataLayerService: DataLayerService,
    private activationService: ActivationService,
    private angulartics2Segment: Angulartics2Segment,
    private router: Router,
    private http: HttpClient,
    private siteSettingsService: SiteSettingsService,
    private ngrxStore: Store<UserState>,
    public snackBar: MatSnackBar,
    public cookieService: CookieService
  ) {}

  public getSecurityQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPasswordActions.GET_SECURITY_QUESTIONS),
      switchMap(() => {
        return this.dataLayerService.getSecurityQuestions().pipe(
          map((questions) => new UserPasswordActions.GetSecurityQuestionsSuccess(questions)),
          catchError((error) => of(new UserPasswordActions.GetSecurityQuestionsFail(error)))
        );
      })
    )
  );

  public resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPasswordActions.RESET_PASSWORD),
      switchMap((action: UserPasswordActions.ResetPassword) => {
        const { email, recaptcha } = action.payload;
        return this.http
          .post(ApiEndpoint.RESET_PASSWORD, {
            email,
            'g-recaptcha-response': recaptcha,
          })
          .pipe(
            map(() => {
              this.router.navigateByUrl('/user/reset-password-request-sent');
              return new UserPasswordActions.ResetPasswordSuccess();
            }),
            catchError((error) => of(new UserPasswordActions.ResetPasswordFailure(error)))
          );
      })
    )
  );

  public getChallengeQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPasswordActions.GET_CHALLENGE_QUESTION),
      switchMap((action: UserPasswordActions.GetChallengeQuestion) => {
        return this.dataLayerService.getChallengeQuestion(action.payload).pipe(
          map((question) => new UserPasswordActions.GetChallengeQuestionSuccess(question)),
          catchError((error) => {
            this.snackBar.openFromComponent(ActivateValidationErrorComponent, {
              duration: 15000,
              data: [{ code: error?.[0]?.code }],
            } as MatSnackBarConfig);

            return of(new UserPasswordActions.GetChallengeQuestionFail(error));
          })
        );
      })
    )
  );

  public setNewPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPasswordActions.SET_NEW_PASSWORD),
      switchMap((action: UserPasswordActions.SetNewPassword) => {
        return this.dataLayerService.setNewPassword(action.payload).pipe(
          map(() => {
            this.activationService.navigateToLogin();
            return new UserPasswordActions.SetNewPasswordSuccess();
          }),
          catchError((error) => of(new UserPasswordActions.SetNewPasswordFail(error)))
        );
      })
    )
  );

  public changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPasswordActions.CHANGE_PASSWORD),
      switchMap((action: UserPasswordActions.ChangePassword) => {
        const { id } = action.payload;
        const url = replaceTemplateKeys(ApiEndpoint.MEMBER_PROFILE_PASSWORD, { id });

        return this.http.post(url, action.payload).pipe(
          map(() => new UserPasswordActions.ChangePasswordSuccess()),
          catchError((error) => of(new UserPasswordActions.ChangePasswordFail(error)))
        );
      })
    )
  );

  public activateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserPasswordActions.ACTIVATE_USER),
      switchMap((action: UserPasswordActions.ActivateUser) => {
        return this.dataLayerService.activateAccount({ ...action.payload, optIns: action.payload.optIns }).pipe(
          map((res) => {
            this.notifySegmentAboutUserActivation(action.payload);

            if (action.payload.isOAuth) {
              // Navigate to OAuth
              window.location.href = `${window.location.origin}${this.siteSettingsService.settings.authentication_provider.login_url}`;
            } else if (res && res.status === 'member_activated') {
              const localData: IUser = {
                isAuthenticated: true,
                preferences: [],
                member_profile_id: parseInt(action.payload.id),
              };

              localStorage.setItem(USER_LOCAL_STORAGE_KEY, JSON.stringify(localData));

              this.siteSettingsService.syncSiteId(res.site_id);

              this.angulartics2Segment.setUserProperties({
                userId: action.payload.id,
                userIdType: 'Member ID',
              });
              setTimeout(() => {
                this.ngrxStore.dispatch(new MemberProfileStore.ViewProfile(parseInt(action.payload.id)));
              });
              window.location.href = `${window.location.origin}/landing`;
            } else {
              // Navigate away to Login page.
              this.activationService.navigateToLogin();
            }

            return new UserPasswordActions.ActivateUserSuccess();
          }),
          catchError((error) => of(new UserPasswordActions.ActivateUserFail(error)))
        );
      })
    )
  );

  /**
   * Let Segment.io know that a user has been activated.
   */
  private notifySegmentAboutUserActivation(actionPayload: UserActivationPayload) {
    this.angulartics2Segment.eventTrack('UserActivated', {
      token: actionPayload.token,
      optInTen: actionPayload.optIns && actionPayload.optIns['tenOptIn'],
      optInClient: actionPayload.optIns && actionPayload.optIns['clientOptIn'],
    });
  }
}
